import dayjs from 'dayjs';
import * as React from 'react';
import { useIntl } from 'react-intl';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  ACTIONS_KEY,
  ADDRESS_KEY,
  AVARAGE_CONSUMPTION_KEY,
  AVARAGE_SPEED_KEY,
  CO2_EMISSION_KEY,
  CUSTOMER_KEY,
  DECLINE_KEY,
  DISTANCE_KEY,
  DRIVER_KEY,
  DURATION_KEY,
  ECO_DRIVE_KEY,
  FUEL_CONSUMPTION_CAN_KEY,
  INCREASE_KEY,
  INFO_FOR_PARKING_KEY,
  INFO_KEY,
  LOCKED_KEY,
  MAXIMAL_SPEED_KEY,
  MILEAGE_END_KEY,
  MIN_OFFSET_LENGTH,
  PRIVATE_KEY,
  RESTRICTED_KEY,
  SOURCE_KEY,
  TIMESTAMP_KEY,
  TYPE_PRIVATE_ACTION,
  UNLOCKED_KEY,
  VEHICLE_KEY,
} from '../constants';
import DetailRow from './DetailRow';
import { IS_COMMENT_PARKING } from '../ActionsForms/AddComment';
import SvgIconService from '../../../Services/SvgIconService';
import TimeStamp from '../TableCell/TimeStamp';
import VehicleCell from '../TableCell/Vehicle';
import IsPrivate from '../TableCell/IsPrivate';
import Address from '../TableCell/Address';
import InfoForRide from '../TableCell/InfoForRide';
import DriverCell from '../TableCell/DriverCell';
import SourceCell from '../TableCell/SourceCell';
import EcoDrive from '../TableCell/EcoDrive';
import InfoForParking from '../TableCell/InfoForParking';
import CustomerCell from '../TableCell/CustomerCell';
import Actions from '../TableCell/Actions';
import {
  ICheckedRecord,
  ICheckedTableItems,
  ILastElementOfTable,
  ITripOverviewParking,
  ITripsOverViewRecord,
  ITripsOverviewColumn,
  ITripsOverviewInfo,
  ITripsOverviewTableData,
} from '../interfaces';
import {
  checkedTableItemsAtom,
  columnsAtom,
  commentAtom,
  driverChangingAtom,
  hasMoreAtom,
  hiddenSummaryItemIndexesAtom,
  loadingDataAtom,
  offsetAtom,
  parkingUsageAtom,
  rideBookDataAtom,
  rideItemAtom,
  rideItemIdsAtom,
  ridePrivacyAtom,
  rideUsageAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import Co2Emission from '../TableCell/Co2Emission';
import useAnalytics from '../../../hooks/useAnalytics';
import { GDPR_VALUE, NOT_AVAILABLE_VALUE, OFF_VALUE, PAID_VALUE } from '../../CmdTable/constants';
import useTable from '../../CmdTable/hooks/useTable';

const restrictedIcon = SvgIconService.adminLock();

interface ITableRow {
  id: string;
  lastElement?: any;
  selectOneRow: (id: string, customerId: string, isRowChecked: boolean, timestamp: string) => void;
  selectedCheckbox: ICheckedRecord[];
  updateTable: () => void;
  allCheckedRowsOpened: boolean | null;
  allCheckedRowsClosed: boolean | null;
  index: number;
}

export default function TableRow(props: ITableRow) {
  const { handleAnonymizedData } = useTable();
  const [rideItem, setRideItem] = useRecoilState(rideItemAtom(props.id));
  const checkedTableItems = useRecoilValue<ICheckedTableItems>(checkedTableItemsAtom);
  const comment = useRecoilValue(commentAtom);
  const driver = useRecoilValue(driverChangingAtom);
  const ridePrivacy = useRecoilValue(ridePrivacyAtom);
  const rideUsage = useRecoilValue(rideUsageAtom);
  const parkingUsage = useRecoilValue(parkingUsageAtom);

  dayjs.extend(weekOfYear);
  const tableData = useRecoilValue<ITripsOverviewTableData | null>(rideBookDataAtom);
  const summaryItemIndexes = useRecoilValue<number[]>(summaryItemIndexesAtom);
  const hiddenSummaryItemIndexes = useRecoilValue<number[]>(hiddenSummaryItemIndexesAtom);
  const loadingNewData = useRecoilValue(loadingDataAtom);
  const [hasMore, setHasMore] = useRecoilState<boolean>(hasMoreAtom);
  const [offset, setOffset] = useRecoilState<number>(offsetAtom);
  const [isRowChecked, setIsRowChecked] = React.useState(false);
  const rideItemIds = useRecoilValue<string[]>(rideItemIdsAtom);
  const { formatMessage: f } = useIntl();
  const columns = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);
  const { trackEvent } = useAnalytics();

  let { current: draggedRef } = React.useRef(false);

  const observer: React.MutableRefObject<any> = React.useRef<HTMLTableRowElement>();

  const lastElementOfTable: ILastElementOfTable = React.useCallback(
    (node) => {
      if (loadingNewData) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          if (tableData) {
            const tableCount = !tableData.count ? tableData.availableCount : tableData.count;
            if (tableData && tableCount !== rideItemIds.length) {
              setHasMore(true);
            }
            if (tableData && tableCount > offset && tableCount - offset >= MIN_OFFSET_LENGTH) {
              if (rideItemIds.length > offset) {
                setOffset((prevOffset) => prevOffset + MIN_OFFSET_LENGTH);
              } else {
                setOffset((prevOffset) => prevOffset);
              }
            } else if (
              tableData &&
              tableCount > offset &&
              tableCount - offset < MIN_OFFSET_LENGTH
            ) {
              setOffset((prevOffset) => prevOffset + (tableCount - prevOffset));
            }
          }
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasMore, loadingNewData]
  );

  const renderCell = (
    column: ITripsOverviewColumn,
    item: ITripsOverViewRecord
  ): JSX.Element | null => {
    switch (column.key) {
      case LOCKED_KEY:
        return (
          <div
            data-cy={`row-${props.index + 1}-${LOCKED_KEY}`}
            className="d-flex align-items-center justify-content-center"
            style={{
              minWidth: '40px',
              maxWidth: '40px',
              width: '40px',
            }}
          >
            {item[column.key] === LOCKED_KEY && (
              <i
                className="cmd-icons cmd-icon-lock text-primary fs-4"
                title={f({ id: 'tables.tripsOverview.icon.tooltip.locked' })}
              />
            )}
            {item[column.key] === UNLOCKED_KEY && (
              <i
                className="cmd-icons cmd-icon-unlock text-success fs-4"
                title={f({ id: 'tables.tripsOverview.icon.tooltip.unlocked' })}
              />
            )}
            {item[column.key] === RESTRICTED_KEY && (
              <div style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                <img
                  src={restrictedIcon}
                  alt="admin-lock"
                  title={f({ id: 'tables.tripsOverview.icon.tooltip.restricted' })}
                />
              </div>
            )}
          </div>
        );
      case TIMESTAMP_KEY:
        if (item) {
          return <TimeStamp data={item} datacyIndex={props.index} />;
        }
        return null;
      case VEHICLE_KEY:
        if (item) {
          return <VehicleCell id={String(item.id)} datacyIndex={props.index} />;
        }
        return null;
      case PRIVATE_KEY:
        if (item) {
          return <IsPrivate data={item.isPrivate} datacyIndex={props.index} />;
        }
        return null;
      case ADDRESS_KEY:
        if (item) {
          return (
            <Address
              start={item.address.start}
              end={item.address.end}
              startLink={item.address.startLink || null}
              endLink={item.address.endLink || null}
              datacyIndex={props.index}
            />
          );
        }
        return (
          <div
            data-cy={`row-${props.index + 1}-Address_Anonymous`}
            className="align-middle"
            style={{ whiteSpace: 'nowrap', minWidth: '350px', flex: 1 }}
          >
            {f({ id: 'enum.gdpr.rides.anonymous' })}
          </div>
        );
      case INFO_KEY:
        if (item) {
          return (
            <InfoForRide
              data={item.info}
              isAnonym={!item.address.startLink}
              datacyIndex={props.index}
            />
          );
        }
        return null;

      case DISTANCE_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-distance`}
              className={`align-middle text-right justify-content-${
                item.distance === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ minWidth: '130px', flex: 1 }}
            >
              {handleAnonymizedData(item.distance)}
            </div>
          );
        }
        return null;

      case DRIVER_KEY:
        if (item) {
          return <DriverCell id={String(item.id)} datacyIndex={props.index} />;
        }
        return null;

      case SOURCE_KEY:
        if (item) {
          return <SourceCell data={item} datacyIndex={props.index} />;
        }
        return null;

      case DURATION_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-duration`}
              className={`align-middle text-right justify-content-${
                item.duration === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ whiteSpace: 'nowrap', minWidth: '180px', flex: 1 }}
            >
              {handleAnonymizedData(item.duration || '')}
            </div>
          );
        }
        return null;

      case ECO_DRIVE_KEY:
        if (
          item &&
          item.ecoScore &&
          item.ecoScore !== GDPR_VALUE &&
          item.ecoScore !== PAID_VALUE &&
          item.ecoScore !== OFF_VALUE &&
          item.ecoScore !== NOT_AVAILABLE_VALUE
        ) {
          return (
            <EcoDrive
              value={item.ecoScore}
              rideId={item.id}
              vehicleId={item.vehicle.id}
              datacyIndex={props.index}
            />
          );
        }
        if (
          item &&
          item.ecoScore &&
          (item.ecoScore === PAID_VALUE ||
            item.ecoScore === OFF_VALUE ||
            item.ecoScore === NOT_AVAILABLE_VALUE)
        ) {
          return (
            <div
              data-cy={`row-${props.index + 1}-ecodrive`}
              className="align-middle text-right justify-content-end"
              style={{
                minWidth: '88px',
                maxWidth: '88px',
                flex: 1,
              }}
            >
              &ndash;
            </div>
          );
        }

        return (
          <div
            data-cy={`row-${props.index + 1}-ecodrive`}
            className="align-middle text-right justify-content-center"
            style={{
              minWidth: '88px',
              maxWidth: '88px',
              flex: 1,
            }}
          >
            <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />
          </div>
        );
      case CO2_EMISSION_KEY:
        if (
          item &&
          item.co2EmissionsCan?.valueCan &&
          String(item.co2EmissionsCan.valueCan) !== GDPR_VALUE &&
          String(item.co2EmissionsCan.valueCan) !== PAID_VALUE &&
          String(item.co2EmissionsCan.valueCan) !== OFF_VALUE &&
          String(item.co2EmissionsCan.valueCan) !== NOT_AVAILABLE_VALUE
        ) {
          return (
            <Co2Emission
              values={item.co2EmissionsCan}
              startDate={item.timestamp.start}
              endDate={item.timestamp.end}
              licenseNumber={item.vehicle.licenceNumber}
              vehicleName={item.vehicle.name}
              datacyIndex={props.index}
            />
          );
        }
        return (
          <div
            data-cy={`row-${props.index + 1}-ecodrive`}
            className="align-middle text-right justify-content-end"
            style={{
              minWidth: '90px',
              maxWidth: '90px',
              flex: 1,
            }}
          >
            &ndash;
          </div>
        );

      case FUEL_CONSUMPTION_CAN_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-fuelConsumption`}
              className={`align-middle text-right justify-content-${
                item.fuelConsumption === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ minWidth: '200px', flex: 1 }}
            >
              {handleAnonymizedData(item.fuelConsumption)}
            </div>
          );
        }
        return null;

      case AVARAGE_CONSUMPTION_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-avgConsumption`}
              className={`align-middle text-right justify-content-${
                item.avgConsumption === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ minWidth: '200px', flex: 1 }}
            >
              {handleAnonymizedData(item.avgConsumption)}
            </div>
          );
        }
        return null;

      case INFO_FOR_PARKING_KEY:
        if (item && item[column.key]) {
          return <InfoForParking data={item} datacyIndex={props.index} />;
        }
        return (
          <div style={{ minWidth: '130px', flex: 1 }}>
            (
            <i
              data-cy={`row-${props.index + 1}-parkingInfo`}
              className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray"
            />
            )
          </div>
        );

      case CUSTOMER_KEY:
        if (item) {
          return <CustomerCell id={String(item.id)} datacyIndex={props.index} />;
        }
        return null;

      case MAXIMAL_SPEED_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-maxSpeed`}
              className={`align-middle text-right justify-content-${
                item.maxSpeed === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{
                minWidth: '100px',
                flex: 1,
              }}
            >
              {handleAnonymizedData(item.maxSpeed)}
            </div>
          );
        }
        return null;

      case AVARAGE_SPEED_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-avgSpeed`}
              className={`align-middle text-right justify-content-${
                item.avgSpeed === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{
                minWidth: '100px',
                flex: 1,
              }}
            >
              {handleAnonymizedData(item.avgSpeed)}
            </div>
          );
        }
        return null;

      case MILEAGE_END_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-mileage`}
              className={`align-middle text-right justify-content-${
                item.mileage === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ minWidth: '100px', flex: 1 }}
            >
              {handleAnonymizedData(item.mileage)}
            </div>
          );
        }
        return null;

      case INCREASE_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-increase`}
              className={`align-middle text-right justify-content-${
                item.increase === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ minWidth: '100px', flex: 1 }}
            >
              {handleAnonymizedData(item.increase)}
            </div>
          );
        }
        return null;

      case DECLINE_KEY:
        if (item) {
          return (
            <div
              data-cy={`row-${props.index + 1}-decline`}
              className={`align-middle text-right justify-content-${
                item.decline === GDPR_VALUE ? 'center' : 'end'
              }`}
              style={{ minWidth: '100px', flex: 1 }}
            >
              {handleAnonymizedData(item.decline)}
            </div>
          );
        }
        return null;

      case ACTIONS_KEY:
        if (item) {
          return (
            <Actions data={item} updateTable={props.updateTable} datacyIndex={Number(props.id)} />
          );
        }
        return null;

      default:
        return null;
    }
  };

  const handleVehicleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = event.currentTarget;

    props.selectOneRow(
      id,
      String(rideItem?.customer.id),
      isRowChecked,
      rideItem?.timestamp?.start || ''
    );
  };

  const isInInterval = React.useCallback(() => {
    if (rideItem) {
      const tempData = { ...checkedTableItems };
      const blackRideIds = tempData.rideIdBlacklist[rideItem.customer.id] || [];
      const intervals = tempData.intervals;
      const intervalsBlacklist = tempData.intervalsBlacklist;
      const mainInterval = tempData.mainCheckbox;
      return (
        (intervals.find(
          (i) =>
            new Date(rideItem.timestamp.start) >= new Date(i.min) &&
            new Date(rideItem.timestamp.start) <= new Date(i.max)
        ) ||
          mainInterval) &&
        !intervalsBlacklist.find(
          (i) =>
            new Date(rideItem.timestamp.start) >= new Date(i.min) &&
            new Date(rideItem.timestamp.start) <= new Date(i.max)
        ) &&
        !blackRideIds.includes(String(rideItem.id))
      );
    }
    return false;
  }, [checkedTableItems, rideItem]);

  React.useMemo(() => {
    if (rideItem) {
      const checkedRideIds = rideItem.customer.id
        ? checkedTableItems.rideIds[rideItem.customer.id]
        : '';
      if (
        (checkedRideIds && rideItem?.id && checkedRideIds.includes(String(rideItem.id))) ||
        isInInterval()
      ) {
        setIsRowChecked(true);
      } else {
        setIsRowChecked(false);
      }
    }
  }, [checkedTableItems.rideIds, isInInterval, rideItem]);

  React.useMemo(() => {
    if (comment && isRowChecked) {
      setRideItem((oldState: ITripsOverViewRecord | null) => {
        if (oldState) {
          const newState = { ...oldState };
          const infoData: ITripsOverviewInfo = { ...oldState.info };
          infoData[comment.type === IS_COMMENT_PARKING ? 'parkingComment' : 'rideComment'] =
            comment.text;
          if (comment.type === IS_COMMENT_PARKING && oldState.parking) {
            const parkingData = { ...oldState.parking };
            parkingData.summary = {
              ...parkingData.summary,
              comment: parkingData.summary ? Number(parkingData.summary.comment) + 1 : 1,
            };
            newState.parking = { ...parkingData };
          } else {
            infoData.summary = {
              ...infoData.summary,
              comment: Number(infoData.summary.comment) + 1,
            };
          }
          newState.info = { ...infoData };
          return newState;
        }
        return null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment]);

  React.useMemo(() => {
    if (driver && isRowChecked) {
      setRideItem((oldState: ITripsOverViewRecord | null) => {
        if (oldState) {
          const newState = { ...oldState };
          const driverData = { ...oldState.driver };
          driverData.id = driver.id;
          driverData.name = driver.name;
          newState.driver = { ...driverData };
          return newState;
        }
        return null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver]);

  React.useMemo(() => {
    if (ridePrivacy && isRowChecked) {
      setRideItem((oldState: ITripsOverViewRecord | null) => {
        if (oldState) {
          const newState = { ...oldState };
          newState.isPrivate = TYPE_PRIVATE_ACTION === ridePrivacy;
          return newState;
        }
        return null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ridePrivacy]);

  const getNewRideUsages = (oldTagIds: string[]) => {
    return rideUsage.filter((i) => !oldTagIds.includes(String(i.id)));
  };

  const getNewParkingUsages = (oldTagIds: string[]) => {
    return parkingUsage.filter((i) => !oldTagIds.includes(String(i.id)));
  };

  React.useMemo(() => {
    if (isRowChecked) {
      setRideItem((oldState: ITripsOverViewRecord | null) => {
        if (oldState) {
          const newState = { ...oldState };
          const infoData: ITripsOverviewInfo = { ...oldState.info };

          const oldTagIds = infoData.rideTags.map((i) => String(i.id));
          const newTags = getNewRideUsages(oldTagIds);
          infoData.rideTags = [...infoData.rideTags, ...newTags];
          infoData.summary = {
            ...infoData.summary,
            tag: Number(infoData.summary.tag) + newTags.length,
          };
          newState.info = { ...infoData };
          return newState;
        }
        return null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideUsage]);

  React.useMemo(() => {
    if (isRowChecked) {
      setRideItem((oldState: ITripsOverViewRecord | null) => {
        if (oldState) {
          const newState = { ...oldState };
          const infoData: ITripsOverviewInfo = { ...oldState.info };
          if (oldState.parking) {
            const parkingData: ITripOverviewParking = { ...oldState.parking };
            const oldTagIds = infoData.parkingTags.map((i) => String(i.id));
            const newTags = getNewParkingUsages(oldTagIds);
            infoData.parkingTags = [...infoData.parkingTags, ...newTags];
            parkingData.summary = {
              ...parkingData.summary,
              tag: Number(parkingData.summary?.tag) + newTags.length,
            };
            newState.parking = { ...parkingData };
          }
          newState.info = { ...infoData };
          return newState;
        }
        return null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingUsage]);

  const renderCheckbox = (vehicleRow: ITripsOverViewRecord) => {
    if (props.selectedCheckbox) {
      props.selectedCheckbox.find(
        (item: ICheckedRecord) => String(item.id) === String(vehicleRow.id)
      );
    }

    const handleStopPropagationCheckbox = (
      event: React.MouseEvent<HTMLLabelElement, MouseEvent>
    ) => {
      event.stopPropagation();
    };

    return (
      <div
        data-cy={`row-${props.index + 1}-checkbox`}
        className="align-middle text-center justify-content-center b-table-prehlad-jazd-checkbox"
        style={{
          minWidth: '40px',
          maxWidth: '40px',
          width: '40px',
        }}
      >
        <div className="e-checkbox checkbox-sm">
          <label
            htmlFor={vehicleRow.id}
            style={{ position: 'relative' }}
            onClick={handleStopPropagationCheckbox}
          >
            <input
              id={vehicleRow.id}
              name={vehicleRow.id}
              type="checkbox"
              checked={isRowChecked}
              onChange={handleVehicleCheckbox}
              data-cy={`ride-book-row-checkbox-${vehicleRow.id}`}
            />
            <span className="p-0" />
          </label>
        </div>
      </div>
    );
  };

  const showInfo = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['ride book', 'click', 'Ride book show info table row']);

    if (draggedRef) {
      return;
    }

    const id = event.currentTarget.getAttribute('data-id');
    const element = event.target as HTMLInputElement;

    if (
      id &&
      rideItem &&
      element.id !== `ride-${id}` &&
      element.id !== `rideActions-${id}` &&
      element.id !== `comment-${id}` &&
      element.id !== `usage-${id}` &&
      element.id !== `driver-${id}` &&
      element.id !== `comment-parking-${id}` &&
      element.id !== `usage-parking-${id}`
    ) {
      setRideItem({ ...rideItem, open: !rideItem.open });
    }
  };

  const getIsRideChecked = () => {
    if (rideItem) {
      const checkedRideIds = rideItem.customer.id
        ? checkedTableItems.rideIds[rideItem.customer.id]
        : [];
      if (
        (checkedRideIds && rideItem?.id && checkedRideIds.includes(String(rideItem.id))) ||
        isInInterval()
      ) {
        return true;
      }
      return false;
    }
    return false;
  };

  React.useEffect(() => {
    if (rideItem && props.allCheckedRowsOpened !== null && getIsRideChecked()) {
      setRideItem({ ...rideItem, open: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.allCheckedRowsOpened]);

  React.useEffect(() => {
    if (rideItem && props.allCheckedRowsClosed !== null && getIsRideChecked()) {
      setRideItem({ ...rideItem, open: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.allCheckedRowsClosed]);

  const handleMouseDown = () => {
    draggedRef = false;
  };

  const handleMouseMove = () => {
    draggedRef = true;
  };

  const getIsInOpenedSection = () => {
    if (summaryItemIndexes.length === 0) {
      return true;
    }
    let sectionId: number | null = null;
    summaryItemIndexes.forEach((value, key) => {
      if (key === summaryItemIndexes.length - 1 && value <= props.index) {
        sectionId = value;
      } else if (value <= props.index && value < summaryItemIndexes[key + 1]) {
        sectionId = value;
      }
    });
    if (sectionId !== null) {
      return !hiddenSummaryItemIndexes.includes(sectionId);
    }
    return true;
  };

  return rideItem && getIsInOpenedSection() ? (
    <React.Fragment key={`${rideItem.vehicle.id}${props.id}`}>
      <div
        style={{ userSelect: 'none' }}
        key={`${rideItem.id}${props.id}`}
        data-id={rideItem.id}
        ref={props.lastElement ? lastElementOfTable : undefined}
        data-cy="ride-book-row-showinfo"
        className="b-table-prehlad-jazd-row-main"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onClick={showInfo}
      >
        {renderCheckbox(rideItem)}
        {columns &&
          columns.map((column) => (
            <React.Fragment key={column.key}>
              {!column.hidden && renderCell(column, rideItem)}
            </React.Fragment>
          ))}
      </div>
      {rideItem.open && (
        <DetailRow
          key={`detail-${rideItem.vehicle}${props.id}`}
          data={rideItem}
          // columnsLength={props.columns.length}
          // updateTable={props.updateTable}
          datacyIndex={props.index}
        />
      )}
    </React.Fragment>
  ) : null;
}
